import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import speedDate from 'speed-date';

let enFormatter = 'MM/DD/YYYY';
let otherFormatter = 'DD.MM.YYYY';

Vue.use(Vuex);

export const store = new Vuex.Store({
    state: {
        prospectuses: [],
        companies: [],
        searchParams: {},
        lastSearchParams: null,
        conf: {},
        disabledDates: {},
        prospectusState: {},
        searching: false,
        prospectusTypesWithListing: [null, 1, 2],
        currentlyLoadingFiles: [],
        companiesReady: false,
        languages: {
            "fi": 0,
            "sv": 1,
            "en": 2
        }
    },
    getters: {
        DisabledDates: state => {
            let output = {};
            if (state.searchParams.PeriodEnd) {
                output.periodStart = {
                    from: state.searchParams.PeriodEnd
                }
            }
            if (state.searchParams.PeriodBegin) {
                output.periodEnd = {
                    to: state.searchParams.PeriodBegin
                }
            }
            return output;
        },
        LocaleString: state => {
            let lang = state.conf.language;
            if (lang === "fi")
                return lang;
            else if (lang === "en")
                return "en-us";
            else if (lang === "sv")
                return "sv-se";
        },
        IsFileLoading: (state) => (fileId) => {
            return state.currentlyLoadingFiles.includes(fileId);
        },
        YesNo: (state) => (value) => {
            if (value === -1)
                return "";
            if (value === 0)
                return state.conf.translations.no;
            if (value === 1)
                return state.conf.translations.yes;
        },
        FormatDate: (state, getters) => (date) => {
            if (getters.LocaleString === "en")
                return speedDate.cached(enFormatter, new Date(date));
            else
                return speedDate.cached(otherFormatter, new Date(date));
        },
    },
    mutations: {
        setProspectuses: (state, newProspectuses) => {
            state.prospectuses = newProspectuses;
        },
        setSearchParams: (state, newSearchParams) => {
            state.searchParams = Object.assign({}, state.searchParams, newSearchParams);
        },
        setConf: (state, newConf) => {
            state.conf = Object.assign({}, state.conf, newConf);
        },
        setCompanies: (state, newCompanies) => {
            state.companies = newCompanies;
            state.companiesReady = true;
        },
        setProspectusTypes: (state, newTypes) => {
            state.prospectusTypes = newTypes;
        },
        setSearching: (state, value) => {
            state.searching = value;
        },
        setLastSearchParams: (state, value) => {
            state.lastSearchParams = value;
        },
        addCurrentlyLoadingFile: (state, value) => {
            state.currentlyLoadingFiles.push(value);
        },
        removeCurrentlyLoadingFile: (state, value) => {
            let index = state.currentlyLoadingFiles.indexOf(value);
            if (index > -1)
                state.currentlyLoadingFiles.splice(index, 1);
        }
    },
    actions: {
        Init: ({ commit, state }, config) => {
            commit('setConf', config);

            // Fetch the companies
            axios.get(state.conf.apiUrl + state.conf.companiesPath, {
                headers: {
                    "Ocp-Apim-Subscription-Key": state.conf.apiKey
                }
            }).then(response => {
                let companies = response.data;
                companies.unshift({
                    companyId: 0,
                    companyName: state.conf.translations.allCorporations
                })
                commit('setCompanies', companies);
            })

            let searchParamsFromSessionStorage = window.sessionStorage.getItem("prospectus3SearchParams");
            if (searchParamsFromSessionStorage) {
                let oldSearchParams = JSON.parse(searchParamsFromSessionStorage);

                if (oldSearchParams.PeriodEnd)
                    oldSearchParams.PeriodEnd = new Date(oldSearchParams.PeriodEnd);
                if (oldSearchParams.PeriodBegin)
                    oldSearchParams.PeriodBegin = new Date(oldSearchParams.PeriodBegin);

                commit('setSearchParams', oldSearchParams);
            }
        },
        Search: ({ commit, state }) => {
            if (state.searching)
                return;

            commit('setSearching', true);

            window.sessionStorage.setItem("prospectus3SearchParams", JSON.stringify(state.searchParams));

            let allowedParameters = Object.keys(state.searchParams).filter(key => {
                if (key === "Listing") {
                    return state.prospectusTypesWithListing.includes(state.searchParams.ProspectusType);
                }
                return true;
            })

            axios.get(state.conf.apiUrl + state.conf.prospectusPath, {
                headers: {
                    "Ocp-Apim-Subscription-Key": state.conf.apiKey
                },
                params: Object.assign({
                    ...Object.keys(state.searchParams)
                        .filter(key => allowedParameters.includes(key))
                        .reduce((obj, key) => {
                            return {
                                ...obj,
                                [key]: state.searchParams[key]
                            };
                        }, {})
                }, { Lang: state.languages[state.conf.language] })
            })
                .then(response => {
                    if (response.data) {
                        commit('setProspectuses', response.data);
                    }
                    commit('setLastSearchParams', state.searchParams);
                    commit('setSearching', false);
                })
                .catch(err => {
                    commit('setSearching', false);
                    console.log(err);
                })
        },
        LoadFile: ({ commit, state }, file) => {
            commit('addCurrentlyLoadingFile', file.fileId);

            // axios({
            //     url: state.conf.apiUrl + state.conf.prospectusFileHeaderPath,
            //     method: 'GET',
            //     headers: {
            //         "Ocp-Apim-Subscription-Key": state.conf.apiKey
            //     },
            //     params: {
            //         FileId: file.fileId,
            //         FileType: file.fileType
            //     }
            // })

            axios({
                url: state.conf.apiUrl + state.conf.prospectusFilePath,
                method: 'GET',
                responseType: 'blob',
                headers: {
                    "Ocp-Apim-Subscription-Key": state.conf.apiKey
                },
                params: {
                    FileId: file.fileId,
                    FileName: file.fileName
                }
            })
                .then(response2 => {
                    let data = new Blob([response2.data]);

                    // For IE
                    if (navigator.msSaveOrOpenBlob) {
                        navigator.msSaveOrOpenBlob(data, file.fileName);
                    }
                    // For proper browsers
                    else {
                        const url = window.URL.createObjectURL(data);
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', file.fileName);
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    }
                    commit('removeCurrentlyLoadingFile', file.fileId);
                })
                .catch(err => {
                    console.log(err);
                    //alert("Error downloading file");
                    commit('removeCurrentlyLoadingFile', file.fileId);
                })
        }
    }
})


// WEBPACK FOOTER //
// ./source/js/Prospectus3Page/src/store.js